import { InternalRefetchQueriesInclude } from '@apollo/client';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { MailIcon } from '@synoptic/ui-kit/icons/react/mail.tsx';
import { UserIcon } from '@synoptic/ui-kit/icons/react/user.tsx';
import {
  ModalContent,
  ModalHeader,
  ModalRoot,
  ModalTitle,
} from '@synoptic/ui-kit/modal/modal.js';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import {
  TabsContent,
  TabsItem,
  TabsList,
  TabsRoot,
} from '@synoptic/ui-kit/tabs/tabs.tsx';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { UserCardFragmentFragment } from '@/__generated__/graphql.ts';
import { listForm } from '@/components/stream-deck/list-form/list-form.css.ts';
import { InviteByEmailModalContent } from '@/components/streams/stream-invite/invite-by-email-modal-content.tsx';
import { InviteByUsernameModalContent } from '@/components/streams/stream-invite/invite-by-username-modal-content.tsx';
import {
  buttonsContainer,
  inviteUserTabItem,
} from '@/components/streams/stream-invite/invite-users-modal.css.ts';
import { useInviteUsersToStream } from '@/components/streams/stream-invite/use-invite-users-to-stream.ts';

export interface InviteUsersFormData {
  users: UserCardFragmentFragment[];
  emails: string[];
}

const MAX_INVITES = 25;

export const InviteUsersModal = ({
  open,
  onOpenChange,
  refetchQueries,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refetchQueries?: InternalRefetchQueriesInclude;
}) => {
  const id = useParams().id as string;
  const isTablet = useIsTablet();
  const [selectedTab, setSelectedTab] = useState('byUsername');

  const { handleSubmit, control, formState } = useForm<InviteUsersFormData>({
    defaultValues: {
      emails: [],
      users: [],
    },
  });

  const [inviteUsersToStream, { loading: inviting }] = useInviteUsersToStream(
    id,
    refetchQueries,
  );

  const submit: SubmitHandler<InviteUsersFormData> = async (data) => {
    const invitees = {
      emails: data.emails,
      inviteeIds: data.users.map((user) => user.id),
    };

    await inviteUsersToStream(invitees);
    onOpenChange(false);
  };

  return (
    <ModalRoot open={open} onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Invite others</ModalTitle>
        </ModalHeader>

        <form className={listForm} onSubmit={handleSubmit(submit)}>
          <TabsRoot value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList>
              <TabsItem value="byUsername" className={inviteUserTabItem}>
                <UserIcon />
                By username
              </TabsItem>
              <TabsItem value="byEmail" className={inviteUserTabItem}>
                <MailIcon />
                By email
              </TabsItem>
            </TabsList>

            <TabsContent value="byUsername">
              <InviteByUsernameModalContent
                control={control}
                formState={formState}
                maxInvites={MAX_INVITES}
              />
            </TabsContent>

            <TabsContent value="byEmail">
              <InviteByEmailModalContent
                control={control}
                formState={formState}
                maxInvites={MAX_INVITES}
              />
            </TabsContent>
          </TabsRoot>

          <div className={buttonsContainer}>
            <Button
              loading={inviting}
              size={isTablet ? 'large' : 'medium'}
              type={'submit'}
            >
              Invite
            </Button>
          </div>
        </form>
      </ModalContent>
    </ModalRoot>
  );
};
